import { Clock, MessageSquare } from 'lucide-react';
import type { ReactElement } from 'react';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { RelativeTime } from '../RelativeTime';
import { CardReferenceCopy } from './CardReferenceCopy';

/**
 * The card back's header line beside "In list …" — owner-directed,
 * `docs/notes/card-back-header-meta.md`: the `KEY-NNNN` reference and its Copy
 * button, how many comments the card has (a button that takes the reader to
 * them), and how long ago anything last happened on the card (`1d`, `23h`), the
 * full instant on hover.
 *
 * ⚠ Rendered through `Dialog`'s `headerAside`, never inside `title`: a control in
 * the title becomes part of the dialog's accessible name, which every test
 * locates the card back by.
 *
 * **"Updated" is `lastActivityAt`** — the newest `activity_entries` row naming
 * the card (`apps/server/src/cards/aging.ts`), so a comment, a description edit,
 * an approval or rejection, a move, a label all count; the card's creation when
 * nothing has happened since. The card socket refetches the detail on every
 * frame, which is what keeps it fresh.
 */
export interface CardBackHeaderMetaProps {
  /** The card's `ref`, as the server scoped it to this viewer; `null` hides it. */
  readonly reference: string | null;
  /**
   * `undefined` while the count loads, and for a card with no comment section
   * (a link card) — then nothing renders, rather than a count that leads nowhere.
   */
  readonly commentCount: number | undefined;
  readonly lastActivityAt: string;
  readonly onShowComments: () => void;
}

export function CardBackHeaderMeta({
  reference,
  commentCount,
  lastActivityAt,
  onShowComments,
}: CardBackHeaderMetaProps): ReactElement {
  const { t } = useInterfaceLanguage();

  return (
    <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
      {reference === null ? null : <CardReferenceCopy reference={reference} />}
      {commentCount === undefined ? null : (
        <button
          type="button"
          data-testid="card-back-comment-count"
          aria-label={t(
            'cardBack.headerMeta.commentCountLabel',
            { count: commentCount },
            commentCount,
          )}
          className="inline-flex items-center gap-1 rounded-sm text-sm tabular-nums text-ink-muted hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          onClick={onShowComments}
        >
          <MessageSquare aria-hidden="true" className="size-3.5" />
          {commentCount}
        </button>
      )}
      <span
        data-testid="card-back-last-updated"
        className="inline-flex items-center gap-1 text-sm tabular-nums text-ink-muted"
      >
        <Clock aria-hidden="true" className="size-3.5" />
        <span className="sr-only">{t('cardBack.headerMeta.lastUpdated')}</span>
        <RelativeTime value={lastActivityAt} />
      </span>
    </span>
  );
}
